import { Link } from "react-router-dom";
import { ArrowLeft, ScrollText } from "lucide-react";

const SECTIONS = [
  {
    n: 1, title: "Acceptance of Terms",
    body: (
      <>
        <p>By using ModNode, you agree to follow these Terms of Service, Discord's Terms of Service, Discord's Community Guidelines, and any applicable laws.</p>
        <p>If you do not agree with these terms, you must stop using ModNode and remove the bot from your server.</p>
      </>
    ),
  },
  {
    n: 2, title: "Our Services",
    body: (
      <>
        <p>ModNode provides Discord moderation, management, and community tools. Features may include moderation commands, warnings, bans, timeouts, logging, automated systems, AI moderation, server management tools, and other features made available by ModNode.</p>
        <p>Features may change, be added, removed, limited, or temporarily unavailable at any time.</p>
      </>
    ),
  },
  {
    n: 3, title: "Server Owner Responsibility",
    body: (
      <>
        <p>The owner and authorized staff of a server are responsible for how ModNode is configured and used in their server.</p>
        <p>ModNode is a tool. Actions performed through the bot may be based on commands, permissions, automations, or settings configured by server staff. ModNode Productions is not responsible for actions taken by a server's owners, administrators, moderators, or other authorized users.</p>
        <p>Server owners are responsible for ensuring that their use of ModNode follows Discord's rules and applicable laws.</p>
      </>
    ),
  },
  {
    n: 4, title: "Proper Use",
    body: (
      <>
        <p>You may not use ModNode to:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Break Discord's Terms of Service or Community Guidelines</li>
          <li>Harass, threaten, abuse, or target others</li>
          <li>Spam or disrupt Discord servers or users</li>
          <li>Attempt to exploit, damage, bypass, or interfere with ModNode systems</li>
          <li>Abuse bugs, vulnerabilities, or unintended features</li>
          <li>Attempt unauthorized access to ModNode accounts, systems, databases, or services</li>
          <li>Use ModNode for illegal or harmful activity</li>
          <li>Misrepresent ModNode as another company, service, or official Discord product</li>
        </ul>
        <p>Users who violate these rules may lose access to ModNode without notice.</p>
      </>
    ),
  },
  {
    n: 5, title: "AI Moderation",
    body: (
      <>
        <p>Some ModNode services may use AI moderation or automated flagging systems to help identify potentially suspicious, harmful, or rule-breaking content.</p>
        <p>AI and automated systems can make mistakes. A flag, warning, or detection should not always be treated as final proof that a user has broken a rule. Server staff are responsible for reviewing information and making final moderation decisions unless an automated action is clearly configured and enabled.</p>
        <p>ModNode does not guarantee that AI moderation will detect every violation or that every detection will be correct.</p>
      </>
    ),
  },
  {
    n: 6, title: "Data and Privacy",
    body: (
      <>
        <p>ModNode may process information necessary to provide its services, including Discord user IDs, server IDs, channel IDs, role IDs, message-related information when required for a feature, moderation records, configuration settings, and other information needed for ModNode to operate.</p>
        <p>ModNode does not claim ownership of your Discord content.</p>
        <p>For more information about how information is handled, please review the ModNode Privacy Policy.</p>
      </>
    ),
  },
  {
    n: 7, title: "Availability",
    body: (
      <>
        <p>ModNode is provided on an "as is" and "as available" basis.</p>
        <p>We do not guarantee that ModNode will always be online, error-free, secure, or available. Services may experience maintenance, outages, bugs, API issues, database issues, updates, or unexpected technical problems.</p>
        <p>ModNode may temporarily or permanently modify, suspend, or discontinue features or services.</p>
      </>
    ),
  },
  {
    n: 8, title: "Updates and Changes",
    body: (
      <>
        <p>ModNode may update these Terms of Service at any time.</p>
        <p>Continued use of ModNode after changes are made means you accept the updated Terms. We recommend checking this page occasionally for updates.</p>
      </>
    ),
  },
  {
    n: 9, title: "Termination of Access",
    body: (
      <>
        <p>ModNode Productions may restrict, suspend, blacklist, or terminate access to ModNode if we believe a user or server has violated these Terms, abused our services, created security risks, or harmed ModNode or its community.</p>
        <p>Where appropriate, we may take action without prior notice.</p>
      </>
    ),
  },
  {
    n: 10, title: "Third-Party Services",
    body: (
      <>
        <p>ModNode may interact with third-party services, including Discord and other APIs or service providers.</p>
        <p>We are not responsible for outages, changes, errors, or actions caused by third-party services. Your use of third-party services may also be subject to their own terms and policies.</p>
      </>
    ),
  },
  {
    n: 11, title: "Limitation of Liability",
    body: (
      <>
        <p>To the maximum extent allowed by law, ModNode Productions and its developers, staff, and contributors are not responsible for damages, losses, moderation decisions, deleted content, lost data, server actions, or other issues resulting from the use or inability to use ModNode.</p>
        <p>You use ModNode at your own risk.</p>
      </>
    ),
  },
  {
    n: 12, title: "Contact and Support",
    body: (
      <>
        <p>If you have questions about these Terms of Service, need support, or want to report a problem, please contact the ModNode team through the official ModNode support server:</p>
        <p>
          <a
            href="https://discord.gg/ModNode"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#8B5CF6] hover:underline"
            data-testid="tos-support-link"
          >
            discord.gg/ModNode
          </a>
        </p>
      </>
    ),
  },
  {
    n: 13, title: "Agreement",
    body: <p>By continuing to use ModNode, you confirm that you have read, understood, and agreed to these Terms of Service.</p>,
  },
];

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-[#06070A] text-white" data-testid="tos-page">
      {/* Top nav */}
      <div className="sticky top-0 z-20 bg-[#06070A]/95 backdrop-blur border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#94A3B8] hover:text-white transition-colors" data-testid="tos-back">
            <ArrowLeft className="w-4 h-4" />
            <span className="font-mono text-xs tracking-widest uppercase">Back</span>
          </Link>
          <div className="flex items-center gap-2 text-[#94A3B8]">
            <ScrollText className="w-4 h-4 text-[#8B5CF6]" />
            <span className="font-display font-black tracking-tight">TERMS OF SERVICE</span>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-3xl mx-auto px-6 py-10 space-y-8">
        <header className="space-y-2">
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight">
            MOD<span className="text-[#8B5CF6]">NODE</span> · Terms of Service
          </h1>
          <p className="text-sm text-[#64748B] font-mono">Last Updated: September 14, 2026</p>
          <p className="text-sm text-[#94A3B8] leading-relaxed">
            By using ModNode, inviting the ModNode bot to your server, using any ModNode website, command, system, or
            service, you agree to these Terms of Service.
          </p>
        </header>

        {SECTIONS.map((s) => (
          <section key={s.n} className="space-y-3 border-t border-white/10 pt-6" data-testid={`tos-section-${s.n}`}>
            <h2 className="font-display text-lg font-bold text-white flex items-baseline gap-3">
              <span className="text-[#8B5CF6] font-mono text-sm">{String(s.n).padStart(2, "0")}</span>
              {s.title}
            </h2>
            <div className="text-sm text-[#E2E8F0] leading-relaxed space-y-3">
              {s.body}
            </div>
          </section>
        ))}

        <footer className="pt-8 border-t border-white/10 space-y-1">
          <p className="font-display font-black text-white">ModNode Productions</p>
          <p className="text-xs text-[#64748B] font-mono">Command your Discord like an operator.</p>
        </footer>
      </div>
    </div>
  );
}
